import getConfig from 'next/config'
import { ImageLoaderProps } from 'next/image'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { breakpoints } from '@awazevr/design-system.utils.react'
import { SupportedBrand } from '../../lib/properties/properties.types'

export function getAssetUrl(path: string): string {
  const { CDN_PREFIX } = getCurrentPublicRuntimeConfig()
  return CDN_PREFIX ? `${CDN_PREFIX}/public${path}` : path
}

export function getImageAssetUrl({ src, width }: ImageLoaderProps): string {
  const imageOpts = `${src}?w=${width}`

  return getAssetUrl(imageOpts)
}

export function isFeatureEnabled(featureToggle: string): boolean {
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  return !!publicRuntimeConfig?.features?.[featureToggle]
}

export function buildSeoCanonical(
  host: string,
  url: string,
): string | undefined {
  if (host === undefined || url === undefined) {
    return undefined
  }

  // Rewrite all homepage or / to just be the host.
  // Rewrite HOS pages e.g. homepage/lodges to just be the host/lodges
  url =
    url === 'homepage' || url === '/' ? '' : `/${url.replace('homepage/', '')}`

  return `https://${host}${url}`
}

export const getBaseUrl = (brand: Brand = 'cottages.com'): string => {
  if (process.env.NODE_ENV === 'production') {
    return ''
  }
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  return publicRuntimeConfig?.[brand]?.baseUrl
}

/**
 * Converts a given URL-like string into an absolute URL when possible.
 *
 * This is used across modules to normalize relative or domain-like links
 * into fully qualified URLs based on the provided `host`.
 *
 * Rules:
 * - Returns the original value if `url` or `host` is missing.
 * - Leaves absolute URLs unchanged (`http://`, `https://`, `//`, or `www.`).
 * - Leaves domain-like patterns unchanged (e.g. `example.com/foo`).
 * - Converts relative paths starting with `/` into absolute URLs.
 * - Uses `http` for localhost, otherwise `https`.
 *
 * @param {string} [url] - The URL string to convert.
 * @param {string} [host] - The host used to build an absolute URL.
 * @returns {string} The absolute URL or the original value if conversion is not possible.
 */
export function convertLinkToAbsoluteUrl(url?: string, host?: string): string {
  // Domain-like patterns such as: example.com/foo or sub.domain.co.uk
  const domainLikePattern = /^[a-z0-9-]+\.[a-z]{2,}(\/|$)/i

  // Relative pattern such as /foo/boo/link
  const relativePattern = /^\/[^\s]*$/i

  if (!url || !host) return url
  const schemeUrl = host.includes('localhost') ? 'http' : 'https'
  try {
    const baseUrl = `${schemeUrl}://${host}`

    const trimmed = url.trim()

    // Already absolute
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('//') ||
      trimmed.toLowerCase().startsWith('www.')
    ) {
      return trimmed
    }

    if (domainLikePattern.test(trimmed)) {
      return trimmed
    }

    if (relativePattern.test(trimmed))
      return new URL(trimmed, baseUrl).toString()
    else return trimmed
  } catch {
    return url
  }
}

/**
 * Recursively walks through an object or array and converts all `href`
 * fields using `convertLinkToAbsoluteUrl`.
 *
 * This is used in multiple modules to ensure that deeply nested structures
 * produce consistent absolute URLs, regardless of where links are located.
 *
 * Behavior:
 * - Ignores null, undefined, primitives, and Date objects.
 * - Recursively processes arrays and plain objects.
 * - Only transforms keys named `"href"` whose value is a string.
 *
 * @template T
 * @param {T} value - The structure to recursively scan for `href` fields.
 * @param {string} [host] - The host used to create absolute URLs.
 * @returns {T} A cloned structure with all `href` fields normalized.
 */
export function deepConvertLinks<T>(value: T, host?: string): T {
  // Return early for null/undefined or primitives
  if (value === null || value === undefined) return value
  if (value instanceof Date) return value
  if (typeof value !== 'object') return value

  // Arrays → recursively process each element
  if (Array.isArray(value)) {
    return value.map((v) => deepConvertLinks(v, host)) as unknown as T
  }

  // Plain objects → recursively process each field
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(value as Record<string, unknown>)) {
    const fieldValue = value[key] as Record<string, unknown>

    // Only transform "href" fields
    if (key === 'href' && typeof fieldValue === 'string') {
      out[key] = convertLinkToAbsoluteUrl(fieldValue, host)
      continue
    }
    out[key] = deepConvertLinks(fieldValue, host)
  }

  return out as T
}

export function getEndOfDayAsUTCString(): string {
  return getEndOfDay().toUTCString()
}

export function getEndOfDay(): Date {
  const now = new Date()
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
    ),
  )
}

export function setExpirationInHours(hours: number): Date {
  const now = new Date()
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours() + hours,
      now.getUTCMinutes(),
      now.getUTCSeconds(),
    ),
  )
}

interface ImageSettings {
  w?: string
  h?: string
  q?: string
  fm?: string
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
}

export const setImageParams = (
  url: string,
  imageSettings: ImageSettings,
): string => {
  if (typeof url === 'string' && url.startsWith('data:')) {
    return url
  }

  const [originpart, searchPart] = url?.split?.('?') || []

  if (!originpart) {
    return ''
  }
  const searchParams = new URLSearchParams(searchPart)

  for (const [param, value] of Object.entries(imageSettings).filter(
    ([, v]) => v !== undefined,
  )) {
    searchParams.set(param, value)
  }

  return `${originpart}?${searchParams}`
}
type BrandConfig = {
  baseUrl: string
  addLocationTagToSearches?: boolean
}

export type PublicRuntimeConfig = {
  hoseasons: BrandConfig
  'cottages.com': BrandConfig
  novasol: BrandConfig
  fincallorca: BrandConfig
  jamesVillas: BrandConfig
  LOCATION_API_KEY: string
  PLACES_API_AKAMAI_KEY: string
  PLACES_API_URI: string
  ACQ_PLACES_API_URI: string
  SUGGESTIONS_ENDPOINT: string
  AKAMAI_KEY: string
  CDN_PREFIX: string | null
  features: {
    newsletter: boolean
    placesApiOptimizelyVariableKey: string
  }
  APP_ENV: 'dev' | 'pprd' | 'prd'
  CONTENTFUL_SPACE_ID: string
  tmsId: string
  guestAppTmsId: string
  RETRIEVE_USER_ROUTE: string
}

export const getCurrentPublicRuntimeConfig = (): PublicRuntimeConfig => {
  const {
    publicRuntimeConfig: {
      APP_ENV,
      CONTENTFUL_SPACE_ID,
      CDN_PREFIX,
      RETRIEVE_USER_ROUTE,
      ...runtimeConfig
    },
  } = getConfig()
  runtimeConfig[APP_ENV].APP_ENV = APP_ENV
  runtimeConfig[APP_ENV].CONTENTFUL_SPACE_ID = CONTENTFUL_SPACE_ID
  runtimeConfig[APP_ENV].CDN_PREFIX = CDN_PREFIX
  runtimeConfig[APP_ENV].RETRIEVE_USER_ROUTE = RETRIEVE_USER_ROUTE
  return runtimeConfig[APP_ENV]
}

export const parseSetCookie = (str: string): Record<string, string> =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce((acc, v) => {
      const key = !v[1] ? 'true' : decodeURIComponent(v[1].trim())
      acc[decodeURIComponent(v[0].trim())] = key
      return acc
    }, {})

export const isLocaleInLocaleList = (
  locale: string,
  localesToCheck: string[],
): boolean => {
  function extractLocale(str: string): string {
    const MAX_LENGTH = 100
    if (str.length > MAX_LENGTH) {
      throw new Error('Input string is too long')
    }
    const match = RegExp(/\(([^)]+)\)$/).exec(str)
    return match ? match[1] : ''
  }

  const localesNotToInclude = localesToCheck?.map((locale) =>
    extractLocale(locale),
  )

  return localesNotToInclude?.includes(locale)
}

export const brandTagOf = (brand: SupportedBrand): string => {
  switch (brand) {
    case 'cottages.com':
      return 'brandCottages'
    case 'hoseasons':
      return 'brandHoseasons'
    case 'novasol':
      return 'brandNovasol'
    case 'fincallorca':
      return 'brandFincallorca'
    case 'jamesVillas':
      return 'brandJamesVillas'
  }
  throw new Error(`Unsupported brand ${brand}`)
}

export const getUserDeviceType = (): string | undefined => {
  const width = typeof window !== 'undefined' ? window.innerWidth : undefined
  if (!width) {
    return undefined
  }
  if (width < breakpoints[0]) {
    return 'mobile'
  }
  if (width < breakpoints[1]) {
    return 'tablet'
  }
  return 'desktop'
}

/**
 * Recursively traverses any input value (object, array, or primitive)
 * and replaces all Contentful "downloads" image URLs with equivalent
 * "images" URLs. A URL is replaced only if:
 *
 *   1. it contains the substring `//downloads.ctfassets.net`
 *   2. it ends with (or includes before query params) a known image file extension:
 *      .jpg, .jpeg, .png, .webp, .gif, .bmp, .tiff, .svg
 *
 * The function preserves the original data shape and returns a new structure.
 * It also collects a list of all URLs that were replaced.
 *
 * @template T
 *
 * @param {T} input - Any value to traverse (object, array, string, etc.).
 *
 * @returns {{
 *   result: T,
 *   replacements: Array<{ before: string, after: string }>
 * }} An object containing:
 *   - `result`: the transformed input with replaced URLs
 *   - `replacements`: a list of all performed URL replacements
 *
 * @example
 * const data = {
 *   img: "//downloads.ctfassets.net/foo/photo.jpg",
 *   nested: ["//downloads.ctfassets.net/bar/icon.svg"]
 * };
 *
 * const { result, replacements } = replaceCtfassetsImageUrls(data);
 *
 * // result.img === "//images.ctfassets.net/foo/photo.jpg"
 * // replacements === [
 * //   { before: "...", after: "..." }
 * // ]
 */
export function replaceCtfassetsImageUrls<T>(input: T): {
  result: T
  replacements: Array<{ before: string; after: string }>
} {
  const imageExtRegex = /\.(jpg|jpeg|png|webp|gif|bmp|tiff|svg)(\?|$)/i

  const replacements: Array<{ before: string; after: string }> = []

  function replaceString(value: string): string {
    const isMatch =
      value.includes('//downloads.ctfassets.net') && imageExtRegex.test(value)

    if (isMatch) {
      const replaced = value.replace(
        '//downloads.ctfassets.net',
        '//images.ctfassets.net',
      )

      replacements.push({ before: value, after: replaced })
      return replaced
    }

    return value
  }

  function traverse<T>(value: T) {
    if (typeof value === 'string') return replaceString(value)
    if (value instanceof Date) return value // ✔ prevent date corruption
    if (Array.isArray(value)) return value.map((v) => traverse(v))
    if (value !== null && typeof value === 'object') {
      const out: unknown = {}
      for (const key in value) out[key] = traverse(value[key])
      return out
    }
    return value
  }

  return {
    result: traverse(input) as T,
    replacements,
  }
}

export function addMissingImageParams(
  url: string,
  params: {
    q: string | number
    w: string
    fm: string
  },
): string {
  let u: URL

  try {
    if (url.startsWith('//images.ctfassets.net')) {
      u = new URL(`https://${url}`)
    } else {
      u = new URL(url)
    }
  } catch {
    return url
  }
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (!u.searchParams.has(key)) {
        u.searchParams.set(key, String(value))
      }
    }
  })

  return u.toString()
}
